<template>
  <div class="min-h-screen bg-[hsl(var(--background))]">
    <!-- Page title bar — always visible against page background -->
    <div class="border-b border-white/10 bg-[#0f1f3d]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
        <div>
          <h1 class="text-xl font-black text-white tracking-wide" style="font-family: var(--font-display)">SEASONS MANAGEMENT</h1>
          <p class="text-sm text-blue-300/70 mt-0.5">Manage softball seasons and schedules</p>
        </div>
        <button
          @click="showCreateForm = true"
          class="flex items-center gap-2 bg-mlb-gold hover:bg-yellow-400 text-mlb-navy-deep font-bold px-4 py-2 rounded-lg text-sm transition-colors shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Season
        </button>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Toast / error banner -->
      <div
        v-if="toastMessage"
        :class="[
          'mb-6 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3',
          toastType === 'error'
            ? 'bg-red-900/40 text-red-200 border border-red-700/60'
            : 'bg-green-900/40 text-green-200 border border-green-700/60'
        ]"
      >
        <span>{{ toastMessage }}</span>
        <button @click="toastMessage = ''" class="text-current opacity-60 hover:opacity-100 shrink-0">✕</button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16 gap-4">
        <div class="w-10 h-10 rounded-full border-2 border-yellow-400/30 border-t-yellow-400 animate-spin" />
        <p class="text-blue-300/60 text-sm">Loading seasons…</p>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="sortedSeasons.length === 0"
        class="flex flex-col items-center justify-center py-20 gap-5 rounded-xl border border-white/10 bg-[#0a1628]"
      >
        <svg class="w-14 h-14 text-yellow-400/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5" />
        </svg>
        <div class="text-center">
          <p class="text-white font-semibold text-lg">No seasons yet</p>
          <p class="text-blue-300/60 text-sm mt-1">Create your first season to start tracking games and stats.</p>
        </div>
        <button
          @click="showCreateForm = true"
          class="flex items-center gap-2 bg-mlb-gold hover:bg-yellow-400 text-mlb-navy-deep font-bold px-5 py-2.5 rounded-lg text-sm transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add First Season
        </button>
      </div>

      <!-- Seasons table -->
      <div v-else class="rounded-xl border border-white/10 overflow-hidden bg-[#0a1628]">
        <div class="px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-white">All Seasons</h2>
          <span class="text-xs text-blue-300/50">{{ sortedSeasons.length }} season{{ sortedSeasons.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full">
            <thead>
              <tr class="border-b border-white/10">
                <th class="px-5 py-3 text-left text-xs font-bold text-blue-300/60 uppercase tracking-wider">Season</th>
                <th class="px-5 py-3 text-left text-xs font-bold text-blue-300/60 uppercase tracking-wider">League</th>
                <th class="px-5 py-3 text-left text-xs font-bold text-blue-300/60 uppercase tracking-wider">Notes</th>
                <th class="px-5 py-3 text-right text-xs font-bold text-blue-300/60 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              <tr v-for="season in sortedSeasons" :key="season.season_id" class="hover:bg-white/5 transition-colors">
                <td class="px-5 py-3 whitespace-nowrap">
                  <span class="text-sm font-semibold text-white">{{ season.year }} {{ SEASON_TYPE_LABELS[season.season_type] }}</span>
                </td>
                <td class="px-5 py-3 whitespace-nowrap text-sm text-blue-200/70">
                  {{ LEAGUE_TYPE_LABELS[season.league_type] }}
                </td>
                <td class="px-5 py-3 text-sm text-blue-300/50 max-w-xs truncate">
                  {{ season.notes || '—' }}
                </td>
                <td class="px-5 py-3 whitespace-nowrap text-right">
                  <button @click="editSeason(season)" class="text-blue-400 hover:text-blue-200 text-xs font-medium mr-4 transition-colors">Edit</button>
                  <button @click="deleteSeason(season.season_id)" class="text-red-400 hover:text-red-200 text-xs font-medium transition-colors">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Create/Edit Season Modal -->
      <div
        v-if="showCreateForm || editingSeason"
        class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4"
        @click.self="closeForm"
      >
        <div class="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1f3d] shadow-2xl p-6">
          <div class="mb-5">
            <h3 class="text-lg font-black text-white tracking-wide" style="font-family: var(--font-display)">
              {{ editingSeason ? 'EDIT SEASON' : 'CREATE NEW SEASON' }}
            </h3>
            <p v-if="displayNamePreview" class="text-sm text-yellow-400 mt-1 font-semibold">
              {{ displayNamePreview }}
            </p>
          </div>

          <form @submit.prevent="editingSeason ? updateSeason() : createSeason()">
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-xs font-bold text-blue-300/70 uppercase tracking-wider mb-1.5">Year</label>
                <input
                  v-model.number="form.year"
                  type="number"
                  min="2000"
                  max="2100"
                  required
                  class="w-full px-3 py-2.5 text-sm rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400/30 transition-colors"
                  placeholder="2026"
                />
              </div>

              <div>
                <label class="block text-xs font-bold text-blue-300/70 uppercase tracking-wider mb-1.5">Season Type</label>
                <select
                  v-model="form.season_type"
                  required
                  class="w-full px-3 py-2.5 text-sm rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400/30 transition-colors"
                >
                  <option value="" class="bg-[#0f1f3d]">Select season…</option>
                  <option v-for="(label, key) in SEASON_TYPE_LABELS" :key="key" :value="key" class="bg-[#0f1f3d]">{{ label }}</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-bold text-blue-300/70 uppercase tracking-wider mb-1.5">League Type</label>
                <select
                  v-model="form.league_type"
                  required
                  class="w-full px-3 py-2.5 text-sm rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400/30 transition-colors"
                >
                  <option value="" class="bg-[#0f1f3d]">Select league…</option>
                  <option v-for="(label, key) in LEAGUE_TYPE_LABELS" :key="key" :value="key" class="bg-[#0f1f3d]">{{ label }}</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-bold text-blue-300/70 uppercase tracking-wider mb-1.5">
                  Notes <span class="text-white/30 normal-case font-normal">(optional)</span>
                </label>
                <input
                  v-model="form.notes"
                  type="text"
                  class="w-full px-3 py-2.5 text-sm rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-yellow-400/60 focus:ring-1 focus:ring-yellow-400/30 transition-colors"
                  placeholder="e.g. District 5 field, Tuesdays 7pm"
                />
              </div>
            </div>

            <div class="flex justify-end gap-3 mt-6">
              <button
                type="button"
                @click="closeForm"
                class="px-4 py-2 text-sm font-medium text-blue-300 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="submitting"
                class="px-5 py-2 text-sm font-bold text-mlb-navy-deep bg-mlb-gold hover:bg-yellow-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ submitting ? 'Saving…' : (editingSeason ? 'Update Season' : 'Create Season') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  seasonsApi,
  type Season,
  type SeasonType,
  type LeagueType,
  SEASON_TYPE_LABELS,
  LEAGUE_TYPE_LABELS,
} from '@/api/seasons'

const loading = ref(false)
const submitting = ref(false)
const toastMessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const seasons = ref<Season[]>([])
const showCreateForm = ref(false)
const editingSeason = ref<Season | null>(null)

const emptyForm = () => ({
  year: new Date().getFullYear(),
  season_type: '' as SeasonType | '',
  league_type: '' as LeagueType | '',
  notes: '',
})

const form = ref(emptyForm())

const displayNamePreview = computed(() => {
  if (!form.value.year || !form.value.season_type || !form.value.league_type) return ''
  return `${form.value.year} ${SEASON_TYPE_LABELS[form.value.season_type as SeasonType]} – ${LEAGUE_TYPE_LABELS[form.value.league_type as LeagueType]}`
})

const sortedSeasons = computed(() =>
  [...seasons.value].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year
    return a.season_type.localeCompare(b.season_type)
  })
)

const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  toastMessage.value = message
  toastType.value = type
  if (type === 'success') setTimeout(() => { toastMessage.value = '' }, 3000)
}

onMounted(loadSeasons)

async function loadSeasons() {
  loading.value = true
  try {
    const res = await seasonsApi.getAll()
    seasons.value = res.data ?? []
  } catch (err: any) {
    showToast(err.response?.data?.error || 'Failed to load seasons', 'error')
    seasons.value = []
  } finally {
    loading.value = false
  }
}

async function createSeason() {
  submitting.value = true
  try {
    await seasonsApi.create({
      year: form.value.year,
      season_type: form.value.season_type as SeasonType,
      league_type: form.value.league_type as LeagueType,
      notes: form.value.notes || undefined,
    })
    showToast('Season created successfully')
    await loadSeasons()
    closeForm()
  } catch (err: any) {
    showToast(err.response?.data?.error || 'Failed to create season', 'error')
  } finally {
    submitting.value = false
  }
}

function editSeason(season: Season) {
  editingSeason.value = season
  form.value = {
    year: season.year,
    season_type: season.season_type,
    league_type: season.league_type,
    notes: season.notes || '',
  }
}

async function updateSeason() {
  if (!editingSeason.value) return
  submitting.value = true
  try {
    await seasonsApi.update(editingSeason.value.season_id, {
      year: form.value.year,
      season_type: form.value.season_type as SeasonType,
      league_type: form.value.league_type as LeagueType,
      notes: form.value.notes || undefined,
    })
    showToast('Season updated successfully')
    await loadSeasons()
    closeForm()
  } catch (err: any) {
    showToast(err.response?.data?.error || 'Failed to update season', 'error')
  } finally {
    submitting.value = false
  }
}

async function deleteSeason(seasonId: number) {
  if (!confirm('Delete this season? This cannot be undone.')) return
  try {
    await seasonsApi.delete(seasonId)
    showToast('Season deleted successfully')
    await loadSeasons()
  } catch (err: any) {
    showToast(err.response?.data?.error || 'Failed to delete season', 'error')
  }
}

function closeForm() {
  showCreateForm.value = false
  editingSeason.value = null
  form.value = emptyForm()
  toastMessage.value = ''
}
</script>
